import React, { useState, useRef, useEffect, useMemo } from 'react';
import ExcelTable from './ExcelTable';
import { tableAPI } from '../services/api';
import './Table.css';

const Table1 = () => {
  // Load saved name from localStorage or use default
  const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
  const [tableName, setTableName] = useState(savedNames[1] || 'A1');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectionStats, setSelectionStats] = useState(null);

  // Sync table name from localStorage when it changes
  useEffect(() => {
    const handleNameUpdate = () => {
      const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
      const newName = savedNames[1] || 'A1';
      setTableName(prevName => {
        if (prevName !== newName) {
          return newName;
        }
        return prevName;
      });
    };
    
    window.addEventListener('defaultTableNameUpdated', handleNameUpdate);
    return () => {
      window.removeEventListener('defaultTableNameUpdated', handleNameUpdate);
    };
  }, []);

  // Get initial data - ONLY headers, don't load from localStorage
  // Data should come from MySQL via ExcelTable component
  const getInitialData = () => {
    // Return empty table with headers only
    // ExcelTable will load actual data from MySQL
    return [['S no', 'Date', 'Code', 'Purpose', 'Currency', 'Amount', 'Cross Profit', 'Expense', 'Net-profit', 'Notes']];
  };

  const initialData = getInitialData();

  // Currency column is at index 4 (0-indexed)
  const currencyColumns = [4];
  // Total columns: Amount (5), Cross Profit (6), Expense (7), Net-profit (8)
  const totalColumns = [];
  
  // Code column is now manual entry (no dropdown)
  const customDropdownColumns = useMemo(() => {
    return {}; // No dropdown columns - Code is manual entry
  }, []);
  
  // Calculate totals from table data, grouped by currency
  // Fetch from MySQL first, fallback to localStorage
  const [tableDataForTotals, setTableDataForTotals] = useState(null);
  
  useEffect(() => {
    const fetchDataForTotals = async () => {
      try {
        // Try MySQL first
        const mysqlData = await tableAPI.getById('table1');
        if (mysqlData && mysqlData.length > 1) {
          setTableDataForTotals(mysqlData);
          // Sync to localStorage
          localStorage.setItem('table_table1', JSON.stringify(mysqlData));
        } else {
          // MySQL empty - clear localStorage and set empty
          localStorage.removeItem('table_table1');
          setTableDataForTotals(null);
        }
      } catch (error) {
        // MySQL failed - try localStorage but validate
        const localData = localStorage.getItem('table_table1');
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            if (parsed && parsed.length > 1) {
              setTableDataForTotals(parsed);
            } else {
              localStorage.removeItem('table_table1');
              setTableDataForTotals(null);
            }
          } catch (e) {
            localStorage.removeItem('table_table1');
            setTableDataForTotals(null);
          }
        } else {
          setTableDataForTotals(null);
        }
      }
    };
    
    fetchDataForTotals();
    // Refresh every 2 seconds to sync with MySQL
    const interval = setInterval(fetchDataForTotals, 2000);
    return () => clearInterval(interval);
  }, []);
  
  const calculateTotals = () => {
    try {
      // Use fetched data
      const parsed = tableDataForTotals;
      if (!parsed || parsed.length < 2) return { amount: 0, crossProfit: 0, expense: 0, netProfit: 0, currencyBreakdown: {} };
      
      const headers = parsed[0];
      const currencyIndex = headers.indexOf('Currency');
      const amountIndex = headers.indexOf('Amount');
      const crossProfitIndex = headers.indexOf('Cross Profit');
      const expenseIndex = headers.indexOf('Expense');
      const netProfitIndex = headers.indexOf('Net-profit');
      
      let amount = 0;
      let crossProfit = 0;
      let expense = 0;
      let netProfit = 0;
      let currencyBreakdown = {};
      
      parsed.slice(1).forEach(row => {
        if (row && row.length > 0) {
          // Get currency from the currency column - must be explicitly set (not empty)
          const rowCurrency = currencyIndex !== -1 && row[currencyIndex] 
            ? String(row[currencyIndex]).toUpperCase().trim() 
            : '';
          
          // Only process if currency column exists and has a value
          if (currencyIndex === -1) {
            // No currency column, skip currency breakdown
            if (amountIndex !== -1 && row[amountIndex]) {
              amount += parseFloat(row[amountIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            }
            if (crossProfitIndex !== -1 && row[crossProfitIndex]) {
              crossProfit += parseFloat(row[crossProfitIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            }
            if (expenseIndex !== -1 && row[expenseIndex]) {
              expense += parseFloat(row[expenseIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            }
            if (netProfitIndex !== -1 && row[netProfitIndex]) {
              netProfit += parseFloat(row[netProfitIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            }
            return; // Skip currency tracking
          }
          
          // Always add to INR totals (main totals)
          if (amountIndex !== -1 && row[amountIndex]) {
            const value = parseFloat(row[amountIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            amount += value;
          }
          if (crossProfitIndex !== -1 && row[crossProfitIndex]) {
            const value = parseFloat(row[crossProfitIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            crossProfit += value;
          }
          if (expenseIndex !== -1 && row[expenseIndex]) {
            const value = parseFloat(row[expenseIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            expense += value;
          }
          if (netProfitIndex !== -1 && row[netProfitIndex]) {
            const value = parseFloat(row[netProfitIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            netProfit += value;
          }
          
          // Only track non-INR currencies that are explicitly set (not empty, not INR)
          if (rowCurrency && rowCurrency !== 'INR' && rowCurrency !== '') {
            if (!currencyBreakdown[rowCurrency]) {
              currencyBreakdown[rowCurrency] = { amount: 0, crossProfit: 0, expense: 0, netProfit: 0 };
            }
            
            // Add values to this currency's breakdown
            if (amountIndex !== -1 && row[amountIndex]) {
              const value = parseFloat(row[amountIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
              currencyBreakdown[rowCurrency].amount += value;
            }
            if (crossProfitIndex !== -1 && row[crossProfitIndex]) {
              const value = parseFloat(row[crossProfitIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
              currencyBreakdown[rowCurrency].crossProfit += value;
            }
            if (expenseIndex !== -1 && row[expenseIndex]) {
              const value = parseFloat(row[expenseIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
              currencyBreakdown[rowCurrency].expense += value;
            }
            if (netProfitIndex !== -1 && row[netProfitIndex]) {
              const value = parseFloat(row[netProfitIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
              currencyBreakdown[rowCurrency].netProfit += value;
            }
          }
        }
      });
      
      // Clean up currency breakdown - remove currencies with all zeros
      Object.keys(currencyBreakdown).forEach(currency => {
        const values = currencyBreakdown[currency];
        if (values.amount === 0 && values.crossProfit === 0 && values.expense === 0 && values.netProfit === 0) {
          delete currencyBreakdown[currency];
        }
      });
      
      return { amount, crossProfit, expense, netProfit, currencyBreakdown };
    } catch (e) {
      console.error('Error calculating totals:', e);
      return { amount: 0, crossProfit: 0, expense: 0, netProfit: 0, currencyBreakdown: {} };
    }
  };

  const totals = useMemo(() => calculateTotals(), [tableDataForTotals]);

  // Reload totals when table data changes (check for actual changes, not just time-based)
  // DISABLED: Don't reload from localStorage every 500ms - data comes from MySQL
  // This was causing data to disappear after loading from MySQL
  // useEffect(() => {
  //   let lastDataHash = '';
  //   const interval = setInterval(() => {
  //     const currentData = localStorage.getItem('table_table1');
  //     const currentHash = currentData ? JSON.stringify(currentData).slice(0, 1000) : '';
  //     
  //     if (currentHash !== lastDataHash) {
  //       lastDataHash = currentHash;
  //       setRefreshKey(prev => prev + 1);
  //     }
  //   }, 500);
  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    // Save updated name to localStorage for sidebar sync
    const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
    savedNames[1] = tableName; // Table1 has id 1
    localStorage.setItem('defaultTableNames', JSON.stringify(savedNames));
    // Dispatch custom event to notify sidebar
    window.dispatchEvent(new Event('defaultTableNameUpdated'));
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditingName(false);
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  return (
    <div className="table-page">
      <div className="table-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                className="table-name-input"
                maxLength={50}
              />
            ) : (
              <h1 onDoubleClick={handleNameDoubleClick} className="table-name-editable" title="Double-click to edit" style={{ margin: 0 }}>
                {tableName}
                <span className="edit-icon">✎</span>
              </h1>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap', marginLeft: 'auto' }}>
              {/* Amount - INR and AED separately */}
              {(() => {
                const inrAmount = totals.amount - (totals.currencyBreakdown && Object.keys(totals.currencyBreakdown).length > 0 
                  ? Object.values(totals.currencyBreakdown).reduce((sum, val) => sum + (val.amount || 0), 0)
                  : 0);
                const aedAmount = totals.currencyBreakdown?.AED?.amount || 0;
                
                return (
                  <>
                    {(inrAmount !== 0 || aedAmount !== 0) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Amount:</span>
                        {inrAmount !== 0 && (
                <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                            INR: ₹{inrAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                        )}
                        {aedAmount !== 0 && (
                          <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                            AED: AED {aedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                        )}
                        {selectionStats && selectionStats[5] && selectionStats[5].hasNumeric && selectionStats[5].byCurrency && (
                          <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {selectionStats[5].byCurrency.INR && (
                              <>
                              <span>Selected INR: ₹{selectionStats[5].byCurrency.INR.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[5].byCurrency.INR.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                            {selectionStats[5].byCurrency.AED && (
                              <>
                              <span>Selected AED: AED {selectionStats[5].byCurrency.AED.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: AED {selectionStats[5].byCurrency.AED.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                          </div>
                        )}
              </div>
                    )}
                  </>
                );
              })()}
              {/* Cross Profit - INR and AED separately */}
              {(() => {
                const inrCrossProfit = totals.crossProfit - (totals.currencyBreakdown && Object.keys(totals.currencyBreakdown).length > 0 
                  ? Object.values(totals.currencyBreakdown).reduce((sum, val) => sum + (val.crossProfit || 0), 0)
                  : 0);
                const aedCrossProfit = totals.currencyBreakdown?.AED?.crossProfit || 0;
                
                return (
                  <>
                    {(inrCrossProfit !== 0 || aedCrossProfit !== 0) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Cross Profit:</span>
                        {inrCrossProfit !== 0 && (
                <span style={{ fontSize: '0.875rem', color: '#8b5cf6', fontWeight: '700' }}>
                            INR: ₹{inrCrossProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                        )}
                        {aedCrossProfit !== 0 && (
                          <span style={{ fontSize: '0.875rem', color: '#8b5cf6', fontWeight: '700' }}>
                            AED: AED {aedCrossProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                        )}
                        {selectionStats && selectionStats[6] && selectionStats[6].hasNumeric && selectionStats[6].byCurrency && (
                          <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {selectionStats[6].byCurrency.INR && (
                              <>
                              <span>Selected INR: ₹{selectionStats[6].byCurrency.INR.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[6].byCurrency.INR.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                            {selectionStats[6].byCurrency.AED && (
                              <>
                              <span>Selected AED: AED {selectionStats[6].byCurrency.AED.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: AED {selectionStats[6].byCurrency.AED.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                          </div>
                        )}
              </div>
                    )}
                  </>
                );
              })()}
              
              {/* Expense - INR and AED separately */}
              {(() => {
                const inrExpense = totals.expense - (totals.currencyBreakdown && Object.keys(totals.currencyBreakdown).length > 0 
                  ? Object.values(totals.currencyBreakdown).reduce((sum, val) => sum + (val.expense || 0), 0)
                  : 0);
                const aedExpense = totals.currencyBreakdown?.AED?.expense || 0;
                
                return (
                  <>
                    {(inrExpense !== 0 || aedExpense !== 0) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Expense:</span>
                        {inrExpense !== 0 && (
                <span style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: '700' }}>
                            INR: ₹{inrExpense.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                        )}
                        {aedExpense !== 0 && (
                          <span style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: '700' }}>
                            AED: AED {aedExpense.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                        )}
                        {selectionStats && selectionStats[7] && selectionStats[7].hasNumeric && selectionStats[7].byCurrency && (
                          <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {selectionStats[7].byCurrency.INR && (
                              <>
                              <span>Selected INR: ₹{selectionStats[7].byCurrency.INR.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[7].byCurrency.INR.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                            {selectionStats[7].byCurrency.AED && (
                              <>
                              <span>Selected AED: AED {selectionStats[7].byCurrency.AED.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: AED {selectionStats[7].byCurrency.AED.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                          </div>
                        )}
              </div>
                    )}
                  </>
                );
              })()}
              
              {/* Net Profit - INR and AED separately */}
              {(() => {
                const inrNetProfit = totals.netProfit - (totals.currencyBreakdown && Object.keys(totals.currencyBreakdown).length > 0 
                  ? Object.values(totals.currencyBreakdown).reduce((sum, val) => sum + (val.netProfit || 0), 0)
                  : 0);
                const aedNetProfit = totals.currencyBreakdown?.AED?.netProfit || 0;
                
                return (
                  <>
                    {(inrNetProfit !== 0 || aedNetProfit !== 0) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Net Profit:</span>
                        {inrNetProfit !== 0 && (
                <span style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '700' }}>
                            INR: ₹{inrNetProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                        )}
                        {aedNetProfit !== 0 && (
                          <span style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '700' }}>
                            AED: AED {aedNetProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                        )}
                        {selectionStats && selectionStats[8] && selectionStats[8].hasNumeric && selectionStats[8].byCurrency && (
                          <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {selectionStats[8].byCurrency.INR && (
                              <>
                              <span>Selected INR: ₹{selectionStats[8].byCurrency.INR.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[8].byCurrency.INR.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                            {selectionStats[8].byCurrency.AED && (
                              <>
                              <span>Selected AED: AED {selectionStats[8].byCurrency.AED.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: AED {selectionStats[8].byCurrency.AED.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                          </div>
                        )}
              </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      <ExcelTable 
        initialData={initialData} 
        currencyColumns={currencyColumns}
        customDropdownColumns={customDropdownColumns}
        fixedHeaders={true}
        totalColumns={totalColumns}
        tableId="table1"
        tableName={tableName}
        onSelectionStatsChange={setSelectionStats}
      />
    </div>
  );
};

export default Table1;

